import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Feather, Send, Loader2, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ease = [0.22, 1, 0.36, 1];

const SUGGESTED = [
  "Who were the earliest of us on this tree?",
  "What stories do we have about our grandparents?",
  "Which places did our family live in?",
  "What did we remember at the memorials?",
];

/**
 * /app/family/:id/council — Ask My Family. A careful historian that answers
 * only from this family's tree, chronicles, feed and memorial guestbooks.
 */
export default function Council() {
  const { id } = useParams();
  const [family, setFamily] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [asking, setAsking] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [famRes, histRes] = await Promise.all([
        api.get(`/v1/families/${id}`),
        api.get(`/v1/council/history`, { params: { family_id: id } }),
      ]);
      setFamily(famRes.data.family);
      setMessages(histRes.data.messages || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  async function ask(question) {
    const q = (question ?? input).trim();
    if (!q || asking) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "", streaming: true }]);
    setAsking(true);
    try {
      const resp = await fetch(`${BACKEND}/api/v1/council/ask`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ family_id: id, question: q }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || "The council could not gather just now.");
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const chunk = JSON.parse(payload);
            setMessages((ms) => {
              const copy = [...ms];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, text: last.text + chunk };
              return copy;
            });
          } catch { /* partial chunk — next pass */ }
        }
      }
    } catch (err) {
      toast.error(err.message || "Something went wrong.");
      setMessages((ms) => ms.filter((m) => !(m.role === "assistant" && m.streaming && !m.text)));
    } finally {
      setMessages((ms) => {
        if (!ms.length) return ms;
        const copy = [...ms];
        copy[copy.length - 1] = { ...copy[copy.length - 1], streaming: false };
        return copy;
      });
      setAsking(false);
    }
  }

  return (
    <section className="mx-auto max-w-3xl px-6 md:px-10 pt-8 pb-24" data-testid="council-page">
      <Link to={`/app/family/${id}`} data-testid="council-back" className="inline-flex items-center gap-2 text-xs text-[color:var(--ink-soft)] link-underline mb-6">
        <ArrowLeft size={12} /> Back to the tree
      </Link>

      <header className="text-center mb-10">
        <div className="overline mb-2">§ The Family Council</div>
        <h1 className="heritage-title text-4xl md:text-5xl leading-tight" data-testid="council-title">
          Ask {family?.name ? `the ${family.name} archive` : "the archive"}
        </h1>
        <p className="mt-3 font-italic-serif italic text-[color:var(--ink-soft)] max-w-lg mx-auto">
          The council reads only what your family has written — the tree, the chronicles, the guestbooks — and answers from that, and nothing more.
        </p>
      </header>

      {/* Conversation */}
      <div className="space-y-6" data-testid="council-messages">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <Sparkles size={22} className="mx-auto text-[color:var(--sepia)]" />
            <p className="mt-3 font-italic-serif italic text-lg text-[color:var(--ink-soft)]">
              Ask anything about the family.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {SUGGESTED.map((s) => (
                <button
                  key={s}
                  data-testid={`council-suggest-${SUGGESTED.indexOf(s)}`}
                  onClick={() => ask(s)}
                  className="border border-[color:var(--sepia)]/40 px-4 py-2 text-sm text-[color:var(--sepia)] hover:bg-[color:var(--parchment-2)] transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        <AnimatePresence initial={false}>
          {messages.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease }}
              className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
              data-testid={`council-msg-${m.role}-${i}`}
            >
              <div
                className={`max-w-[85%] px-5 py-4 ${
                  m.role === "user"
                    ? "bg-[color:var(--sepia)] text-[color:var(--parchment)]"
                    : "border border-[color:var(--sepia)]/30 bg-[color:var(--parchment)]"
                }`}
              >
                {m.role === "assistant" && (
                  <div className="overline text-[color:var(--sepia)] mb-2 flex items-center gap-1.5">
                    <Feather size={11} /> The Council
                  </div>
                )}
                <p className={`text-[16px] leading-[1.65] whitespace-pre-wrap ${m.role === "assistant" ? "font-italic-serif" : ""}`}>
                  {m.text}
                  {m.streaming && <span className="inline-block w-2 h-4 ml-1 bg-[color:var(--rust)]/70 animate-pulse align-middle" />}
                </p>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        <div ref={bottomRef} />
      </div>

      {/* Ask */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          ask();
        }}
        className="mt-10 flex gap-3 items-end border-t border-[color:var(--sepia)]/25 pt-5"
        data-testid="council-form"
      >
        <input
          data-testid="council-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          maxLength={600}
          placeholder="Ask about a name, a place, a year…"
          className="flex-1 bg-transparent border-b border-[color:var(--sepia)]/50 py-2.5 text-[16px] focus:outline-none focus:border-[color:var(--rust)]"
        />
        <button
          type="submit"
          data-testid="council-ask"
          disabled={asking || !input.trim()}
          className="btn-wood !py-2.5 disabled:opacity-50"
        >
          {asking ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Ask
        </button>
      </form>
      <p className="mt-3 numeric-index text-center text-[color:var(--ink-soft)]">
        Answers come only from this family's own records — never from the outside world.
      </p>
    </section>
  );
}
